import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { FieldMessage, useFieldIds } from "../FormField/FormField";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /**
   * Contenido del label. Es `ReactNode` y no `string` a propósito: el
   * checkbox de términos lleva enlaces dentro ("Acepto los <a>términos</a>").
   */
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  className?: string;
  /** Clases del contenedor completo (no de la caja). */
  fieldClassName?: string;
}

/**
 * Checkbox con label rico.
 *
 * El label va enlazado por `htmlFor`, así que el área de clic incluye el
 * texto. Un enlace DENTRO del label sigue funcionando: el estándar dice
 * que activar contenido interactivo dentro de un `<label>` NO activa el
 * control asociado, así que "Acepto los [términos]" navega al hacer clic
 * en el enlace y marca la casilla al hacer clic en el resto del texto.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, error, id, className, fieldClassName, disabled, ...rest },
  ref,
) {
  const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
    hasHint: Boolean(hint),
    hasError: Boolean(error),
  });

  return (
    <div className={cn("grid gap-1.5", fieldClassName)}>
      <div className="flex items-start gap-2.5">
        <input
          ref={ref}
          id={controlId}
          type="checkbox"
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-[5px] border bg-ds-surface",
            "accent-ds-violet-pop transition",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-60",
            error ? "border-ds-terracotta" : "border-ds-border-strong",
            className,
          )}
          {...rest}
        />
        <label
          htmlFor={controlId}
          className={cn(
            "cursor-pointer text-[12px] leading-relaxed text-ds-ink-3",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          {label}
        </label>
      </div>

      <FieldMessage
        error={error}
        hint={hint}
        errorId={errorId}
        hintId={hintId}
      />
    </div>
  );
});
